<template>
  <div :class="`timeline-card-container ${color}-border`">
    <div
      :class="[
        'user-permission',
        'is-size-7',
        emphasizeHeader ? 'emphasize-header' : 'has-text-grey'
      ]"
    >
      {{ headerRight }}
    </div>
    <div class="cols">
      <div v-if="iconName">
        <NrIcon
          aria-hidden="true"
          v-if="iconName"
          :class="{
            'thumbtack-rotated': isThumbtackIcon,
            [`${color}-icon`]: true
          }"
          :name="iconName"
        />
      </div>
      <div v-else class="m-left-24" />
      <div class="content">
        <div class="header">
          <span>{{ header }}</span>
          <span v-if="collapsable">
            <NrIconButton
              @click="handleCollapse"
              :aria-label="`${
                collapse ? 'Collapse' : 'Expand'
              } ${header} timeline event`"
              :aria-controls="`timeline-card-content-${id}`"
              :aria-expanded="collapse"
              size="small"
              variant="minimal-neutral"
              :icon="{
                name: collapse ? 'chevron-down' : 'chevron-up',
                type: 'regular'
              }"
            />
          </span>
        </div>
        <div v-if="subheader" class="subheader">
          <span>{{ subheader }}</span>
        </div>
        <div
          class="timeline-card-content-body"
          :id="`timeline-card-content-${id}`"
        >
          <slot />
        </div>
      </div>
    </div>
    <div class="content-footer is-size-7 has-text-grey">
      <div>{{ footerLeft }}</div>
      <div v-if="withActions" class="icons-bar actions">
        <NrTooltip label="Change visibility" v-if="changeVisibilityIcon">
          <NrIconButton
            variant="minimal-neutral"
            v-if="changeVisibilityIcon"
            :icon="{ name: 'eye', type: 'regular' }"
            size="small"
            @click="$emit('on-visibility', id)"
            aria-label="Change visibility"
          />
        </NrTooltip>
        <NrTooltip v-if="canPin" :label="pinNoteLabel">
          <NrIconButton
            ref="thumbtack"
            :class="[
              { 'thumbtack-rotated': pinned },
              { pinned: pinned },
              'pin'
            ]"
            variant="minimal-neutral"
            :icon="{ name: 'Thumbtack', type: 'regular' }"
            size="small"
            @click="handlePinClick(id)"
            :aria-label="pinNoteLabel"
          />
        </NrTooltip>
        <NrTooltip label="Edit timeline entry" v-if="editIcon">
          <NrIconButton
            variant="minimal-neutral"
            :icon="{ name: 'pencil-alt', type: 'solid' }"
            size="small"
            v-if="editIcon"
            @click="$emit('on-edit', id)"
            aria-label="Edit timeline entry"
          />
        </NrTooltip>
        <NrTooltip label="Delete" v-if="deleteIcon">
          <NrIconButton
            variant="minimal-neutral"
            size="small"
            :icon="{ name: 'trash-alt', type: 'regular' }"
            v-if="deleteIcon"
            @click="$emit('on-delete', id)"
            aria-label="Delete timeline entry"
          />
        </NrTooltip>
      </div>
    </div>
  </div>
</template>

<script>
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip'

export default {
  components: { NrIcon, NrTooltip, NrIconButton },
  name: 'TimelineCard',
  props: {
    id: {
      type: Number
    },
    changeVisibilityIcon: {
      type: Boolean,
      default: false
    },
    color: {
      type: String
    },
    deleteIcon: {
      type: Boolean,
      default: false
    },
    editIcon: {
      type: Boolean,
      default: false
    },
    pinned: {
      type: Boolean,
      default: false
    },
    canPin: {
      type: Boolean,
      default: false
    },
    headerRight: {
      type: String
    },
    header: {
      type: String
    },
    subheader: {
      type: String
    },
    iconName: {
      type: String,
      default: ''
    },
    footerLeft: {
      type: String
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    withActions: {
      type: Boolean,
      default: true
    },
    emphasizeHeader: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.collapse = this.collapsed
  },
  data() {
    return {
      collapse: false
    }
  },
  computed: {
    pinNoteLabel() {
      return this.pinned ? 'Unpin a note' : 'Pin a note'
    },
    isThumbtackIcon() {
      return this.iconName === 'Thumbtack'
    }
  },
  methods: {
    handlePinClick(id) {
      this.$emit('on-pin-click', id)
    },
    handleCollapse() {
      this.collapse = !this.collapse
      this.$emit('on-collapse', this.collapse)
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
@import '@nextrequestco/components/src/styles/tokens/_variables.scss';

.timeline-card-container {
  background: white;
  position: relative;
  padding: 8px;
  width: 100%;
  border: 1px solid #f4f4f6;
  .actions {
    display: none;
    .icon {
      margin-right: 8px;
      &:hover {
        color: #007ac2;
      }
    }
  }
  .user-permission {
    position: absolute;
    text-align: right;
    clear: both;
    float: right;
    right: 20px;
  }
  .emphasize-header {
    color: $color-green-100;
    font-weight: 600;
  }
  .thumbtack-rotated {
    transform: rotate(90deg);
  }
  .pin {
    transition: rotate 1s;
  }
  .pinned {
    animation-duration: 1s;
    animation-name: pin;
  }
  @keyframes pin {
    75% {
      transform: translateY(-7px);
      rotate: 90deg;
    }
  }
  .cols {
    display: flex;
    .content {
      width: 100%;
      padding-left: 8px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .header {
        margin: 0;
        padding-bottom: 4px;
        font-size: 1.25em;
        font-weight: bolder;
        .chevron-btn {
          background-color: transparent;
          border: none;
          outline: none;
          height: 2px;
        }
        .collapse-btn {
          font-size: 0.75em;
          bottom: 6px;
        }
        .collapse-icon {
          width: 20px;
          height: 14px;
          svg {
            transform: translate(0, 7px);
          }
        }
      }
      .subheader {
        font-weight: $font-weight-semibold;
      }
    }
    .icon {
      transform: scale(0.8);
    }
    .green-icon {
      color: #00854d;
      margin-top: $spacing-6;
    }
    .orange-icon {
      color: #ff573d;
      margin-top: $spacing-6;
    }
  }
  .content-footer {
    width: 100%;
    display: flex;
    padding-left: 24px;
    justify-content: space-between;
    align-items: flex-end;
    padding-top: 12px;
  }
  &:hover {
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.19),
      0 6px 6px rgba(0, 0, 0, 0.23);
  }
  .actions {
    display: flex;
  }
}

.timeline-card-container.green-border:hover {
  border: 1px solid #00854d;
}
.timeline-card-container.orange-border:hover {
  border: 1px solid #ff573d;
}
.timeline-card-content-body {
  p {
    margin-bottom: 0;
  }
  a {
    color: $blue;

    &:active,
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
}
</style>
